import { config } from 'config';
import { generalApi, GeneralApiTagTypes } from 'core/api';
import i18n from 'core/translation/i18next/i18next';
import {
    ActualCommissionSchema,
    actualCommissionSchema,
    CommissionSchema,
    type CommissionsPageSchema,
    commissionsPageSchema,
    CommissionsTableSchema,
    CommissionSummarySchema,
    PostEnquiryEmailResponse,
    SendCreateDeliveryConfirmationPdfSchema,
    SendCreateLoadingConfirmationPdfSchema,
    SendCreateNeutralizationPdfSchema,
    SendCreateOrderCarrierPdfSchema,
    SendInquirySchema,
    SendUniversalOrderEmailSchema,
    TListCommissionNumberResponseBody,
    TListCommissionNumbersRequestBody,
    TPostDeliveryConfirmationEmailRequestBody,
    TPostDeliveryConfirmationEmailResponseBody,
    TPostLoadingConfirmationEmailRequestBody,
    TPostLoadingConfirmationEmailResponseBody,
    TPostNeutralizationConfirmationEmailRequestBody,
    TPostNeutralizationConfirmationEmailResponseBody,
    TPostOrderCarrierConfirmationEmailRequestBody,
    TPostOrderCarrierConfirmationEmailResponseBody,
    TPostOrderConfirmationEmailRequestBody,
    TPostOrderConfirmationEmailResponseBody,
} from 'modules/commissions';
import { QueryParams } from 'types/query-params';

import { TransformedCommissionData } from '../utils/FormattingCommissionData/types';
export interface Message {
    event: 'invalidate';
    data: 'Carriers';
}

export const commissionsApi = generalApi.injectEndpoints({
    endpoints: (builder) => ({
        getCommissions: builder.query<CommissionsPageSchema, QueryParams<CommissionsTableSchema>>({
            query: (params) => ({
                url: config.api.endpoints.commissions,
                params,
            }),
            providesTags: () => [GeneralApiTagTypes.commission.list],
            transformResponse: (res: CommissionsPageSchema) => {
                const validatedSchema = commissionsPageSchema().safeParse(res);
                // used for back compatibility; almost all commissions are incorrect
                if (!validatedSchema.success) {
                    console.warn('Validation failed in getCommissions');
                }
                return res;
            },
        }),
        getCommissionSummary: builder.query<CommissionSummarySchema, QueryParams<CommissionsTableSchema>>({
            query: (params) => ({
                url: config.api.endpoints.commissionSummary,
                params,
            }),
            providesTags: () => [GeneralApiTagTypes.commission.summary],
        }),
        getOneCommission: builder.query<ActualCommissionSchema, QueryParams<{ id: number }>>({
            providesTags: (_res, _err, { id }) => [{ type: GeneralApiTagTypes.commission.item, id }],
            query: ({ id = '' }) => ({
                url: `${config.api.endpoints.commissions}/${id}`,
            }),
            transformResponse: (res: ActualCommissionSchema) => {
                const validatedSchema = actualCommissionSchema(i18n.t).safeParse(res);
                // used for back compatibility; almost all commissions are incorrect
                if (!validatedSchema.success) {
                    console.warn('Validation failed in getCommission');
                }
                return res;
            },
        }),
        postNewCommission: builder.mutation<CommissionSchema, { data: TransformedCommissionData }>({
            query: ({ data }: { data: TransformedCommissionData }) => ({
                url: config.api.endpoints.commissions,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_res, error) =>
                error ? [] : [GeneralApiTagTypes.commission.list, GeneralApiTagTypes.commission.summary],
        }),
        putUpdatedCommission: builder.mutation<CommissionSchema, { data: TransformedCommissionData; id: number }>({
            query: ({ data, id }) => ({
                url: `${config.api.endpoints.commissions}/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_, error, arg) =>
                error
                    ? []
                    : [
                          GeneralApiTagTypes.commission.list,
                          { type: GeneralApiTagTypes.commission.item, id: arg.id },
                          GeneralApiTagTypes.commission.summary,
                      ],
        }),
        deleteCommission: builder.mutation<CommissionSchema, { id: number }>({
            query: ({ id }) => ({
                url: `${config.api.endpoints.commissions}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_, error) =>
                error ? [] : [GeneralApiTagTypes.commission.list, GeneralApiTagTypes.commission.summary],
        }),
        // createOrderCarrier - Objednávka na dopravce
        postOrderCarrier: builder.query({
            providesTags: ['Commissions'],
            query: ({ body }) => ({
                url: config.api.endpoints.orderCarrier,
                method: 'POST',
                body,
            }),
        }),
        postOrderCarrierPdf: builder.query<
            Blob,
            { body: SendCreateOrderCarrierPdfSchema; aborter?: () => AbortSignal }
        >({
            providesTags: () => [GeneralApiTagTypes.commission.pdf],
            query: ({ body, aborter }: { body: SendCreateOrderCarrierPdfSchema; aborter?: () => AbortSignal }) => {
                const signal = aborter?.();
                return {
                    url: config.api.endpoints.orderCarrierPdf,
                    method: 'POST',
                    body,
                    signal,
                    responseHandler: (response) => response.blob(),
                };
            },
        }),
        postOrderCarrierConfirmationEmailOld: builder.query({
            providesTags: () => [GeneralApiTagTypes.commission.email],
            query: ({ body }: { body: SendUniversalOrderEmailSchema }) => ({
                url: config.api.endpoints.orderCarrierConfirmationEmail,
                method: 'POST',
                body,
            }),
        }),
        postOrderCarrierConfirmationEmail: builder.mutation<
            TPostOrderCarrierConfirmationEmailResponseBody,
            TPostOrderCarrierConfirmationEmailRequestBody
        >({
            query: (body) => ({
                url: config.api.endpoints.orderCarrierConfirmationEmail,
                method: 'POST',
                body: body,
            }),
        }),
        //  createOrderConfirmation - Potvrzení objednávky
        postOrderConfirmationEmail: builder.mutation<
            TPostOrderConfirmationEmailResponseBody,
            TPostOrderConfirmationEmailRequestBody
        >({
            query: (body) => ({
                url: config.api.endpoints.orderConfirmationEmail,
                method: 'POST',
                body: body,
            }),
        }),
        // createLoadingConfirmation - Potvrzení nakládky
        postLoading: builder.query({
            providesTags: ['Commissions'],
            query: ({ body }: { body: SendCreateLoadingConfirmationPdfSchema }) => ({
                url: config.api.endpoints.orderLoading,
                method: 'POST',
                body,
            }),
        }),
        postLoadingPdf: builder.query<
            Blob,
            { body: SendCreateLoadingConfirmationPdfSchema; aborter?: () => AbortSignal }
        >({
            providesTags: () => [GeneralApiTagTypes.commission.pdf],
            query: ({ body, aborter }) => {
                const signal = aborter?.();
                return {
                    url: config.api.endpoints.orderLoadingPdf,
                    method: 'POST',
                    signal,
                    body,
                    responseHandler: (response) => response.blob(),
                };
            },
        }),
        postLoadingConfirmationEmail: builder.mutation<
            TPostLoadingConfirmationEmailResponseBody,
            TPostLoadingConfirmationEmailRequestBody
        >({
            query: (body) => ({
                url: config.api.endpoints.orderLoadingConfirmationEmail,
                method: 'POST',
                body: body,
            }),
        }),
        // createDeliveryConfirmation - Potvrzení doručení zboží
        postDelivery: builder.query({
            providesTags: ['Commissions'],
            query: ({ body }: { body: SendCreateDeliveryConfirmationPdfSchema }) => ({
                url: config.api.endpoints.orderDelivery,
                method: 'POST',
                body,
            }),
        }),
        postDeliveryPdf: builder.query<
            Blob,
            { body: SendCreateDeliveryConfirmationPdfSchema; aborter?: () => AbortSignal }
        >({
            providesTags: () => [GeneralApiTagTypes.commission.pdf],
            query: ({ body, aborter }) => {
                const signal = aborter?.();
                return {
                    url: config.api.endpoints.orderDeliveryPdf,
                    method: 'POST',
                    body,
                    signal,
                    responseHandler: (response) => response.blob(),
                };
            },
        }),
        postDeliveryConfirmationEmail: builder.mutation<
            TPostDeliveryConfirmationEmailResponseBody,
            TPostDeliveryConfirmationEmailRequestBody
        >({
            query: (body) => ({
                url: config.api.endpoints.orderDeliveryConfirmationEmail,
                method: 'POST',
                body: body,
            }),
        }),
        postNeutralizationPdf: builder.query<
            Blob,
            { body: SendCreateNeutralizationPdfSchema; aborter?: () => AbortSignal }
        >({
            providesTags: ['Commissions'],
            query: ({ body, aborter }) => {
                aborter?.();
                return {
                    url: config.api.endpoints.orderDischargePdf,
                    method: 'POST',
                    body,
                    responseHandler: (response) => response.blob(),
                };
            },
        }),
        postNeutralizationConfirmationEmail: builder.mutation<
            TPostNeutralizationConfirmationEmailResponseBody,
            TPostNeutralizationConfirmationEmailRequestBody
        >({
            query: (body) => ({
                url: config.api.endpoints.orderDischargeConfirmationEmail,
                method: 'POST',
                body: body,
            }),
        }),
        postInquiryEmail: builder.mutation<PostEnquiryEmailResponse, { body: SendInquirySchema }>({
            invalidatesTags: (_result, _error, { body }) => [{ type: 'CommissionItem', id: body.commissionId }],
            query: ({ body }) => ({
                url: config.api.endpoints.carrierInquiry,
                method: 'POST',
                body,
            }),
        }),
        listCommissionNumbers: builder.query<TListCommissionNumberResponseBody, TListCommissionNumbersRequestBody>({
            query: ({ search, age }) => {
                return {
                    url: config.api.endpoints.commissionListNumbers,
                    method: 'GET',
                    params: {
                        ...(search ? { search } : {}),
                        ...(age ? { age } : {}),
                    },
                };
            },
            providesTags: (_, __, arg) => [{ type: 'CommissionNumbers', id: arg.search }],
        }),
    }),
});

export const {
    useGetCommissionsQuery,
    useGetOneCommissionQuery,
    usePostNewCommissionMutation,
    usePutUpdatedCommissionMutation,
    useDeleteCommissionMutation,
    useLazyPostOrderCarrierQuery,
    useLazyPostOrderCarrierPdfQuery,
    usePostOrderCarrierConfirmationEmailMutation,
    usePostOrderConfirmationEmailMutation,
    useLazyPostLoadingQuery,
    useLazyPostLoadingPdfQuery,
    useGetCommissionSummaryQuery,
    usePostLoadingConfirmationEmailMutation,
    useLazyPostDeliveryPdfQuery,
    useLazyPostDeliveryQuery,
    usePostDeliveryConfirmationEmailMutation,
    useLazyPostNeutralizationPdfQuery,
    usePostNeutralizationConfirmationEmailMutation,
    usePostInquiryEmailMutation,
    useListCommissionNumbersQuery,
    useLazyListCommissionNumbersQuery,
} = commissionsApi;
